import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  project_name: "",
  project_type: "",
  crew_size: "",
  shoot_dates: "",
  requirements: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.requirements) {
      toast.error("Name, email and requirements are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Brief received. We'll be in touch within one business day.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not send your inquiry. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-px bg-[#E2B464]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E2B464]">
                06 — Brief us
              </span>
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Tell us about{" "}
              <span className="italic text-[#E2B464]">your shoot.</span>
            </h2>
            <p className="mt-8 text-zinc-400 leading-relaxed text-base max-w-md">
              Send us a brief - locations, dates, crew size and we'll come back
              within one business day with availability, references.
            </p>

            <div className="mt-12 space-y-5">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                  Address
                </p>
                <p className="text-white">
                  Paradise Studios
                  <br />
                  2587–2615 Gibbston Valley Highway
                  <br />
                  Gibbston, Central Otago 9371, NZ
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                  Production Desk
                </p>
                <a
                  href="mailto:bookings@paradisestudiosnz.com"
                  className="text-white hover:text-[#E2B464] transition-colors"
                >
                  bookings@paradisestudiosnz.com
                </a>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                  Recce Line · 24/7
                </p>
                <p className="text-white">+64 21 133 0045</p>
              </div>
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <Field label="Name *">
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="contact-name-input"
                className="bg-[#0a0a0a] border-white/10 rounded-none h-12 focus-visible:ring-[#E2B464] focus-visible:ring-1 focus-visible:ring-offset-0"
                placeholder="Your full name"
                required
              />
            </Field>
            <Field label="Email *">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                data-testid="contact-email-input"
                className="bg-[#0a0a0a] border-white/10 rounded-none h-12 focus-visible:ring-[#E2B464] focus-visible:ring-1 focus-visible:ring-offset-0"
                placeholder="you@production.com"
                required
              />
            </Field>
            <Field label="Production Co.">
              <Input
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                data-testid="contact-company-input"
                className="bg-[#0a0a0a] border-white/10 rounded-none h-12 focus-visible:ring-[#E2B464] focus-visible:ring-1 focus-visible:ring-offset-0"
                placeholder="Studio / agency"
              />
            </Field>
            <Field label="Phone">
              <Input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                data-testid="contact-phone-input"
                className="bg-[#0a0a0a] border-white/10 rounded-none h-12 focus-visible:ring-[#E2B464] focus-visible:ring-1 focus-visible:ring-offset-0"
                placeholder="+64 ..."
              />
            </Field>
            <Field label="Project name">
              <Input
                value={form.project_name}
                onChange={(e) => set("project_name", e.target.value)}
                data-testid="contact-project-input"
                className="bg-[#0a0a0a] border-white/10 rounded-none h-12 focus-visible:ring-[#E2B464] focus-visible:ring-1 focus-visible:ring-offset-0"
                placeholder="Working title"
              />
            </Field>
            <Field label="Type">
              <Select
                value={form.project_type}
                onValueChange={(v) => set("project_type", v)}
              >
                <SelectTrigger
                  data-testid="contact-type-select"
                  className="bg-[#0a0a0a] border-white/10 rounded-none h-12 text-white focus:ring-[#E2B464] focus:ring-1 focus:ring-offset-0"
                >
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white rounded-none">
                  <SelectItem value="feature">Feature Film</SelectItem>
                  <SelectItem value="tv">TV / Episodic</SelectItem>
                  <SelectItem value="commercial">Commercial / TVC</SelectItem>
                  <SelectItem value="documentary">Documentary</SelectItem>
                  <SelectItem value="stills">Stills / Editorial</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Crew size">
              <Input
                value={form.crew_size}
                onChange={(e) => set("crew_size", e.target.value)}
                data-testid="contact-crew-input"
                className="bg-[#0a0a0a] border-white/10 rounded-none h-12 focus-visible:ring-[#E2B464] focus-visible:ring-1 focus-visible:ring-offset-0"
                placeholder="e.g. 60 pax"
              />
            </Field>
            <Field label="Shoot dates">
              <Input
                value={form.shoot_dates}
                onChange={(e) => set("shoot_dates", e.target.value)}
                data-testid="contact-dates-input"
                className="bg-[#0a0a0a] border-white/10 rounded-none h-12 focus-visible:ring-[#E2B464] focus-visible:ring-1 focus-visible:ring-offset-0"
                placeholder="e.g. March 4–22, 2026"
              />
            </Field>
            <Field label="Requirements *" full>
              <Textarea
                value={form.requirements}
                onChange={(e) => set("requirements", e.target.value)}
                data-testid="contact-requirements-input"
                className="bg-[#0a0a0a] border-white/10 rounded-none min-h-[140px] focus-visible:ring-[#E2B464] focus-visible:ring-1 focus-visible:ring-offset-0"
                placeholder="Brief synopsis, location requirements, weather contingency, base camp needs…"
                required
              />
            </Field>
            <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Replies within 1 business day
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="btn-primary px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send brief"}{" "}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
